import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '@/api/growth/season/api';
import PageHeader from '@components/common/PageHeader';
import { toastWarning } from '@components/common/Toast/CustomToast';
import { Alert } from '@utils/alert';
import ActionButton from '@/components/common/ActionButton/ActionButton';
import { SeasonUpdateReq } from '@/api/growth/season/type';
import DatePicker from '@/components/common/DatePicker';

const SeasonDetail = () => {
    // const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        userIdx: 0,
        userId: '',
        userName: '',
        gsIdx: 0,
        name: '',
        startDt: '',
        endDt: '',
        regDt: '',
        updDt: '',
    });

    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);

    const { gsIdx } = useParams<{ gsIdx: string }>();

    // 날짜 문자열을 Date 객체로 변환 (YYYY-MM-DD 형식)
    const parseDate = (dateString: string): Date | null => {
        if (!dateString) return null;
        const date = new Date(dateString);
        return isNaN(date.getTime()) ? null : date;
    };

    // 날짜를 YYYY-MM-DD 형식으로 변환
    const formatDateString = (date: Date | null): string => {
        if (!date) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const getSeason = useCallback(async () => {
        const result = await api.seasonInfo(Number(gsIdx));

        // 폼 데이터 초기화
        setFormData(prev => ({
            ...prev,
            userIdx: result.userIdx || 0,
            userId: result.userId || '',
            userName: result.userName || '',
            gsIdx: result.gsIdx || 0,
            name: String(result.name) || '',
            startDt: String(result.startDt) || '',
            endDt: String(result.endDt) || '',
            regDt: result.regDt || '',
            updDt: result.updDt || '',
        }));

        // Date 객체로 변환하여 DatePicker에 설정
        setStartDate(parseDate(String(result.startDt)));
        setEndDate(parseDate(String(result.endDt)));
    }, [gsIdx]);

    const handleInputChange = (field: string, value: string | number) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    // 날짜 변경 핸들러
    const handleStartDateChange = (date: Date | null) => {
        setStartDate(date);
        const dateString = formatDateString(date);
        handleInputChange('startDt', dateString);
    };

    const handleEndDateChange = (date: Date | null) => {
        setEndDate(date);
        const dateString = formatDateString(date);
        handleInputChange('endDt', dateString);
    };

    // 유효성 검사
    const validateForm = async () => {
        // 필수 필드 검사
        if (!formData.name.trim()) {
            toastWarning('작기명을 입력해주세요.');
            return false;
        }
        if (!formData.startDt.trim()) {
            toastWarning('작기시작일을 입력해주세요.');
            return false;
        }
        if (!formData.endDt.trim()) {
            toastWarning('작기종료일을 입력해주세요.');
            return false;
        }

        // 날짜 유효성 검사
        if (startDate && endDate && startDate >= endDate) {
            toastWarning('작기종료일은 작기시작일보다 늦어야 합니다.');
            return false;
        }

        return true;
    };

    // 저장 핸들러
    const handleSave = async () => {
        // 유효성 검사
        const isValid = await validateForm();
        if (!isValid) return;

        await Alert.confirmAndExecute(
            '저장하시겠습니까?',
            '잘못된 정보가 없는지 주의하세요.',
            true,
            async () => {
                const updateRequest: SeasonUpdateReq = {
                    gsIdx: formData.gsIdx,
                    name: formData.name,
                    startDt: formData.startDt,
                    endDt: formData.endDt,
                };

                await api.seasonUpdate(updateRequest).then(async () => {
                    await getSeason();
                });
            },
        );
    };

    const handleCancel = (): void => {
        //const returnUrl = searchParams.get('returnUrl');

        // if (returnUrl) {
        //     // returnUrl이 있으면 해당 URL로 이동 (기존 필터 상태 유지)
        //     navigate(`/season${decodeURIComponent(returnUrl)}`);
        // } else {
        // returnUrl이 없으면 기본 목록 페이지로
        navigate('/season');
        // }
    };

    const handleDelete = async () => {
        await Alert.confirmAndExecute(
            '삭제를 진행하시겠습니까?',
            '삭제후 복구가 불가능합니다. 주의하세요.',
            true,
            async () => {
                await api.seasonDelete(formData.gsIdx).then(async () => {
                    handleCancel();
                });
            },
        );
    };

    useEffect(() => {
        getSeason();
    }, [getSeason]);

    return (
        <>
            <PageHeader
                title="생육작기 상세정보"
                breadcrumbs={[
                    { label: '홈', href: '/' },
                    { label: '생육작기관리' },
                    { label: '생육작기 상세정보', active: true },
                ]}
            />

            <div className="card custom-card mt-4">
                <div className="card-header">
                    <div className="card-title">
                        생육작기 정보를 입력해주세요.
                    </div>
                </div>
                <div className="card-body">
                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                작기번호<span className="text-danger">*</span>:
                            </label>
                        </div>
                        <div className="col-md-10">
                            <input
                                type="text"
                                className="form-control"
                                disabled
                                value={formData.gsIdx || ''}
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                대행사번호<span className="text-danger">*</span>
                                :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <input
                                type="text"
                                className="form-control"
                                disabled
                                value={formData.userIdx || ''}
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                대행사명<span className="text-danger">*</span> :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <input
                                type="text"
                                className="form-control"
                                disabled
                                value={formData.userName}
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                아이디<span className="text-danger">*</span> :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <input
                                type="text"
                                className="form-control"
                                disabled
                                value={formData.userId}
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                작기명<span className="text-danger">*</span> :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="작기명을 입력해주세요."
                                value={formData.name}
                                onChange={e =>
                                    handleInputChange('name', e.target.value)
                                }
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                작기시작<span className="text-danger">*</span> :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <DatePicker
                                selectedDate={startDate}
                                onChange={handleStartDateChange}
                                placeholder="작기 시작일을 선택해주세요"
                                maxDate={endDate || undefined}
                                showYearDropdown={false}
                                showMonthDropdown={false}
                                dropdownMode="scroll"
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                작기종료<span className="text-danger">*</span> :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <DatePicker
                                selectedDate={endDate}
                                onChange={handleEndDateChange}
                                placeholder="작기 종료일을 선택해주세요"
                                minDate={startDate || undefined}
                                showYearDropdown={false}
                                showMonthDropdown={false}
                                dropdownMode="scroll"
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                등록일자<span className="text-danger">*</span> :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <input
                                type="text"
                                className="form-control"
                                value={formData.regDt}
                                disabled
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                갱신일자 :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <input
                                type="text"
                                className="form-control"
                                value={formData.updDt}
                                disabled
                            />
                        </div>
                    </div>
                </div>

                <div className="card-footer">
                    <div className="btn-list text-end">
                        <ActionButton type="save" onClick={handleSave} />
                        <ActionButton type="delete" onClick={handleDelete} />
                        <ActionButton type="cancel" onClick={handleCancel} />
                    </div>
                </div>
            </div>
        </>
    );
};

export default SeasonDetail;
